import { memo } from "react";
import { Clock, Layers, List } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

type ExploreMode = "events" | "sources" | "recently-viewed";

interface ExploreModeTabsProps {
  exploreMode: ExploreMode;
  recentlyViewedCount: number;
  onExploreModeChange?: (mode: ExploreMode) => void;
}

function ExploreModeTabsInner({
  exploreMode,
  recentlyViewedCount,
  onExploreModeChange,
}: ExploreModeTabsProps) {
  return (
    <div
      className="flex w-full min-w-0 items-end gap-5 overflow-x-auto whitespace-nowrap"
      data-testid="mode-tabs"
      role="tablist"
      aria-label="Explore mode"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onExploreModeChange?.("events")}
            role="tab"
            aria-selected={exploreMode === "events"}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 border-b-[3px] px-0 pb-3 pt-1 text-[13px] leading-none transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
            style={exploreMode === "events"
              ? { color: "#165a47", fontWeight: 700, borderBottomColor: "#1D9E75" }
              : { color: "#61584a", fontWeight: 500, borderBottomColor: "transparent" }}
            data-testid="tab-events"
          >
            <List size={12} strokeWidth={exploreMode === "events" ? 2.5 : 2} />
            Events List
          </button>
        </TooltipTrigger>
        <TooltipContent>Browse upcoming events</TooltipContent>
      </Tooltip>

      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onExploreModeChange?.("sources")}
            role="tab"
            aria-selected={exploreMode === "sources"}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 border-b-[3px] px-0 pb-3 pt-1 text-[13px] leading-none transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
            style={exploreMode === "sources"
              ? { color: "#165a47", fontWeight: 700, borderBottomColor: "#1D9E75" }
              : { color: "#61584a", fontWeight: 500, borderBottomColor: "transparent" }}
            data-testid="tab-sources"
          >
            <Layers size={12} strokeWidth={exploreMode === "sources" ? 2.5 : 2} />
            By Source
          </button>
        </TooltipTrigger>
        <TooltipContent>Browse event sources</TooltipContent>
      </Tooltip>

      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onExploreModeChange?.("recently-viewed")}
            role="tab"
            aria-selected={exploreMode === "recently-viewed"}
            className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 border-b-[3px] px-0 pb-3 pt-1 text-[13px] leading-none transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
            style={exploreMode === "recently-viewed"
              ? { color: "#165a47", fontWeight: 700, borderBottomColor: "#1D9E75" }
              : { color: "#61584a", fontWeight: 500, borderBottomColor: "transparent" }}
            data-testid="tab-recently-viewed"
          >
            <Clock size={12} strokeWidth={exploreMode === "recently-viewed" ? 2.5 : 2} />
            Recent
            {recentlyViewedCount > 0 && (
              <span
                style={exploreMode === "recently-viewed"
                  ? { background: "#e4e5e7", color: "#6f7782", fontSize: 11, fontWeight: 600, padding: "4px 8px", borderRadius: 999, marginLeft: 2 }
                  : { background: "#e4e5e7", color: "#6f7782", fontSize: 11, fontWeight: 600, padding: "4px 8px", borderRadius: 999, marginLeft: 2 }}
              >
                {recentlyViewedCount}
              </span>
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>Events you've clicked on this session</TooltipContent>
      </Tooltip>
    </div>
  );
}

export const ExploreModeTabs = memo(ExploreModeTabsInner);
